import { useEffect, useState } from 'react';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import useWishlist from '../../hooks/useWishlist';
import { fetchProducts } from '../../utils/api';

const currency = () => window.themeData?.currency ?? '$';

export default function WishlistPage() {
  const { items, toggle, clearAll, count } = useWishlist();
  const [products, setProducts]  = useState([]);
  const [loading,  setLoading]   = useState(false);

  useEffect(() => {
    if (!items.length) { setProducts([]); return; }
    setLoading(true);
    fetchProducts({ include: items.join(','), per_page: 100 })
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [items.join(',')]);

  if (!count && !loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 py-20">
        <div className="w-24 h-24 bg-accent1/10 rounded-full flex items-center justify-center mb-6">
          <Heart className="w-10 h-10 text-accent1" />
        </div>
        <h2 className="font-black text-3xl text-textDark mb-3">Your wishlist is empty</h2>
        <p className="text-textLight mb-8">Save products you love by clicking the heart icon.</p>
        <a
          href={window.themeData?.siteUrl ? `${window.themeData.siteUrl}/shop/` : '/shop/'}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primaryHover text-white font-bold px-8 py-3 rounded-2xl transition-colors"
        >
          Browse Shop
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-black text-3xl text-textDark flex items-center gap-3">
          <Heart className="w-8 h-8 text-accent1 fill-accent1" />
          My Wishlist
          <span className="text-lg font-bold text-textLight">({count})</span>
        </h1>
        {count > 0 && (
          <button
            onClick={clearAll}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Clear all
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: Math.min(count, 8) }).map((_, i) => (
            <div key={i} className="rounded-3xl bg-gray-100 animate-pulse h-72" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((product) => {
            const price        = parseFloat(product.price ?? 0);
            const regularPrice = parseFloat(product.regular_price ?? 0);
            const onSale       = product.on_sale && regularPrice > price;
            const image        = product.images?.[0]?.src;
            const permalink    = product.permalink ?? '#';

            return (
              <div key={product.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group relative flex flex-col">
                {/* Remove from wishlist */}
                <button
                  onClick={() => toggle(product.id)}
                  className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-accent1 text-white flex items-center justify-center shadow-sm hover:bg-red-500 transition-colors"
                  title="Remove from wishlist"
                >
                  <Heart className="w-4 h-4 fill-current" />
                </button>

                <a href={permalink} className="block">
                  <div className="h-48 bg-gray-50 flex items-center justify-center p-6 overflow-hidden group-hover:bg-gray-100 transition-colors">
                    {image
                      ? <img src={image} alt={product.name} className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-500" />
                      : <span className="text-6xl">🎁</span>
                    }
                  </div>
                </a>

                <div className="p-4 flex flex-col flex-grow">
                  <a href={permalink}>
                    <h3 className="font-bold text-textDark text-sm mb-2 line-clamp-2 hover:text-primary transition-colors">
                      {product.name}
                    </h3>
                  </a>
                  <div className="mt-auto flex items-center justify-between">
                    <div>
                      <span className="font-black text-lg text-primary">{currency()}{price.toFixed(2)}</span>
                      {onSale && (
                        <span className="text-xs text-gray-400 line-through ml-1">{currency()}{regularPrice.toFixed(2)}</span>
                      )}
                    </div>
                    <a
                      href={permalink}
                      className="w-9 h-9 bg-primary hover:bg-primaryHover text-white rounded-xl flex items-center justify-center transition-colors"
                      title="View product"
                    >
                      <ShoppingCart className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
